import React, { useState, useEffect } from 'react';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Users, Plus, Search, Edit, Loader2, CheckCircle, Clock, Building2, ArrowUpDown } from 'lucide-react';
import TenantFilter from '../components/TenantFilter';

const roleLabels = {
  mestre: { label: 'Master', color: 'bg-purple-100 text-purple-700' },
  adm: { label: 'Administrador', color: 'bg-blue-100 text-blue-700' },
  financeiro: { label: 'Financeiro', color: 'bg-green-100 text-green-700' },
  gestor_operacional: { label: 'Gestor Operacional', color: 'bg-orange-100 text-orange-700' },
  operacional: { label: 'Operacional', color: 'bg-slate-100 text-slate-700' },
  prestador: { label: 'Prestador', color: 'bg-amber-100 text-amber-700' },
  api_access: { label: 'API Access', color: 'bg-cyan-100 text-cyan-700' }
};

const UsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const { selectedTenant, tenants } = useTenant();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'operacional',
    password: '',
    tenant_cnpj: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [token, selectedTenant]);

  const fetchUsers = async () => {
    try {
      const tenantParam = selectedTenant && selectedTenant !== 'all' ? `?tenant_cnpj=${selectedTenant}` : '';
      const data = await api.get(`/users${tenantParam}`, token);
      setUsers(data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${editingUser.id}`, updateData, token);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await api.post('/users', formData, token);
        toast.success('Usuário criado com sucesso');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/users/${userId}/approve`, {}, token);
      toast.success('Usuário aprovado com sucesso');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao aprovar usuário');
    }
  };

  const handleToggleActive = async (targetUser) => {
    try {
      await api.put(`/users/${targetUser.id}`, { active: !(targetUser.active !== false) }, token);
      toast.success(targetUser.active !== false ? 'Usuário inativado' : 'Usuário ativado');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      password: '',
      tenant_cnpj: user.tenant_cnpj || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', phone: '', role: 'operacional', password: '', tenant_cnpj: currentUser?.tenant_cnpj || '' });
  };

  const filteredUsers = users
    .filter(u =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Usuários
          </h1>
          <p className="text-slate-500 mt-1">{users.length} usuários cadastrados</p>
        </div>
        <div className="flex items-center gap-3">
          <TenantFilter />
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Usuário
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Public Sans' }}>
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="user-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Perfil</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels)
                      .filter(([key]) => !['mestre', 'api_access'].includes(key) || currentUser?.role === 'mestre')
                      .map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
              
              {/* Campo Tenant - Apenas Master pode definir */}
              {currentUser?.role === 'mestre' && (
                <div className="space-y-2">
                  <Label>Organização (CNPJ)</Label>
                  <Select 
                    value={formData.tenant_cnpj || 'none'} 
                    onValueChange={(v) => setFormData({ ...formData, tenant_cnpj: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="user-tenant-select">
                      <SelectValue placeholder="Selecione a organização" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem organização (Master)</SelectItem>
                      {tenants.map((t) => (
                        <SelectItem key={t.id} value={t.cnpj}>
                          {t.trading_name || t.company_name} ({t.cnpj})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Define a qual organização este usuário pertence
                  </p>
                </div>
              )}
              
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">
                  {editingUser ? 'Atualizar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-52" data-testid="sort-users">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
                <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
                <SelectItem value="created_desc">Cadastro (mais recente)</SelectItem>
                <SelectItem value="created_asc">Cadastro (mais antigo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Perfil</th>
                  <th>Organização</th>
                  <th>Aprovação</th>
                  <th>Criado em</th>
                  <th>Ativo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const roleConfig = roleLabels[user.role];
                  return (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td className="font-medium text-slate-900">{user.name}</td>
                      <td className="text-slate-600">{user.email}</td>
                      <td className="text-slate-600">{user.phone || '-'}</td>
                      <td>
                        <Badge className={roleConfig?.color || 'bg-slate-100'}>
                          {roleConfig?.label || user.role}
                        </Badge>
                      </td>
                      <td className="text-slate-600 text-sm">
                        {user.tenant_name || user.tenant_cnpj || <span className="text-slate-400">-</span>}
                      </td>
                      <td>
                        {user.approved !== false ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" /> Aprovado
                          </Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleApprove(user.id)}
                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                          >
                            <Clock className="w-3 h-3 mr-1" /> Aprovar
                          </Button>
                        )}
                      </td>
                      <td className="text-slate-500 text-sm font-mono">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Switch checked={user.active !== false} onCheckedChange={() => handleToggleActive(user)} data-testid={`toggle-active-user-${user.id}`} />
                          <Badge className={user.active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {user.active !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
